/**
 * Configuration utility for dynamic API base URL determination
 */

import { isLocalHostname, localApiBaseUrl, resolvePciApiTarget } from '@otp-session/shared';

const API_PATH = '/api/card-view-request';

interface ApiConfig {
  baseUrl: string;
  environment: string; // 'local' | 'plan' | 'production' | 'qa' | 'staging' | 'dev' | etc.
}

/**
 * Determines the API base URL based on the current domain
 */
export const getApiConfig = (): ApiConfig => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Check for environment variable override
  const apiOverride = process.env.REACT_APP_API_OVERRIDE;
  if (apiOverride) {
    return {
      baseUrl: apiOverride,
      environment: hostname.includes('localhost') ? 'local' : 'production'
    };
  }

  // Local development
  if (isLocalHostname(hostname)) {
    return {
      baseUrl: localApiBaseUrl(API_PATH),
      environment: 'local'
    };
  }

  // Production and staging environments - the static -> service mapping lives
  // in @otp-session/shared
  const pciTarget = resolvePciApiTarget(API_PATH, { hostname, protocol });
  if (pciTarget) {
    return {
      baseUrl: pciTarget.baseUrl,
      environment: pciTarget.stage || 'production'
    };
  }

  // Fallback to localhost for unknown domains
  return {
    baseUrl: localApiBaseUrl(API_PATH),
    environment: 'local'
  };
};

/**
 * Get the full API endpoint URL
 */
export const getApiUrl = (endpoint: string): string => {
  const config = getApiConfig();
  return `${config.baseUrl}${endpoint}`;
};

/**
 * Get current environment
 */
export const getEnvironment = (): string => {
  return getApiConfig().environment;
};

/**
 * Check if running in local development
 */
export const isLocalDevelopment = (): boolean => {
  return getApiConfig().environment === 'local';
};

/**
 * Detect if the user is on a mobile device
 */
export { isMobileDevice } from '@otp-session/shared';